import {
  type IProvidedTranslationProps,
  withTranslations,
} from '@wix/wixstores-client-common-components/dist/es/src/outOfIframes/translations';
import {useEnvironment, useExperiments} from '@wix/yoshi-flow-editor';
import classNames from 'classnames';
import React, {type FC} from 'react';
import {A11y, EffectFade, Keyboard} from 'swiper/modules';
import {Swiper, SwiperSlide} from 'swiper/react';
import {type ProvidedGlobalProps, withGlobalProps} from '../../../../providers/globalPropsProvider';
import {SPECS} from '../../../../specs';
import {useProductGallery} from '../../ProductGalleryProvider/ProductGalleryProvider';
import s from './MainMediaSwiper.scss';

export enum MainMediaSwiperDataHooks {
  Root = 'main-media-swiper-root',
}

export interface MainMediaSwiperProps extends ProvidedGlobalProps, IProvidedTranslationProps {
  media: JSX.Element[];
  swipeToScroll: boolean;
  isMobile: boolean;
  setSwiper: (swiper: any) => void;
}

const MainMediaSwiperComponent: FC<MainMediaSwiperProps> = ({
  media,
  swipeToScroll,
  isMobile,
  setSwiper: propsSetSwiper,
}) => {
  const {experiments} = useExperiments();
  const {changeSelectedIndex} = useProductGallery();
  const {isRTL} = useEnvironment();
  const isNavigationPerBreakpointEnabled = experiments.enabled(SPECS.PPNavigationSectionPerBreakpoint);
  const isMainMediaNavArrowsEnabled = experiments.enabled(SPECS.ProductPageMainMediaNavigationArrows);

  return (
    <div
      data-hook={MainMediaSwiperDataHooks.Root}
      className={classNames(s.root, {
        [s.isNavigationPerBreakpointEnabled]: isNavigationPerBreakpointEnabled,
      })}>
      <Swiper
        dir={isRTL ? 'rtl' : 'ltr'}
        onSwiper={propsSetSwiper}
        modules={[EffectFade, Keyboard, A11y]}
        effect={'fade'}
        fadeEffect={{crossFade: true}}
        loop={isMainMediaNavArrowsEnabled}
        slidesPerView={1}
        allowTouchMove={swipeToScroll || isMobile}
        keyboard={{enabled: true}}
        threshold={5}
        touchStartPreventDefault={false}
        onSlideChange={(swiperInstance) =>
          changeSelectedIndex(isMainMediaNavArrowsEnabled ? swiperInstance.realIndex : swiperInstance.activeIndex)
        }
        className={s.swiperContainer}>
        {media.map((child, index) => (
          <SwiperSlide
            key={index}
            className={classNames(s.swiperSlide, {
              [s.isNavigationPerBreakpointEnabled]: isNavigationPerBreakpointEnabled,
            })}>
            {child}
          </SwiperSlide>
        ))}
      </Swiper>
    </div>
  );
};

export const MainMediaSwiper = withGlobalProps(withTranslations('globals.texts')(MainMediaSwiperComponent));
